// ReportPreview — tabbed preview panel in the Queue page editor.
// "PDF view" tab: the report is rendered server-side (Cloudflare Worker /
// render pipeline); in the browser we show a placeholder because the
// EndAdvertiserReport template lives in src/reports/ (outside the web/ tree)
// and cross-tree imports are prohibited by tsconfig.web.json. Task 13 can
// wire a "render preview" API call here if live preview is needed.
// "Email body" tab: shows the prose commentary or the email body override.

import { useState } from "react";

// ─── Types (mirrored from src/reports/EndAdvertiserReport.tsx) ─────────────
// We re-declare only what ReportPreview needs so tsconfig.web.json stays
// self-contained (include: ["web/**"]). The actual runtime component is
// imported via the re-export shim below.

interface Totals {
  spend: number;
  leads: number;
  cpl: number | null;
  clicks?: number;
  impressions?: number;
  ctr?: number | null;
  cpm?: number | null;
}

interface Period {
  start: string;
  end: string;
}

interface TopCampaign {
  campaign_id: string;
  spend: number;
  leads: number;
  cpl: number | null;
}

interface TopAdSet {
  adset_id: string;
  adset_name: string | null;
  spend: number;
  leads: number;
  cpl: number | null;
}

export interface ReportPayload {
  client_id: string;
  account_id: string;
  display_name: string;
  currency: string;
  cadence: "weekly" | "monthly";
  period: Period;
  prior_period: Period;
  has_prior_period: boolean;
  totals: Totals;
  prior_totals: Totals;
  deltas: { spend_arrow: "up" | "down" | "flat"; leads_arrow: "up" | "down" | "flat"; cpl_arrow: "up" | "down" | "flat" };
  top_campaigns: TopCampaign[];
  top_adsets: TopAdSet[];
  alert_counts: { critical: number; warning: number; info: number };
}

export interface ReportCtx {
  endAdvertiserName: string;
  preparedBy: string;
  periodStart: string;
  periodEnd: string;
  currency: string;
  cadence: "weekly" | "monthly";
  commentary?: string;
}

interface Props {
  payload: ReportPayload;
  ctx: ReportCtx;
  /** null = use ctx.commentary as email body */
  emailBody: string | null;
}

type Tab = "pdf" | "email";

function tabBtnStyle(active: boolean): React.CSSProperties {
  return {
    padding: "6px 14px",
    fontSize: "var(--fs-xs)",
    fontFamily: "var(--font-mono)",
    textTransform: "uppercase",
    letterSpacing: "var(--letter-label)",
    fontWeight: 600,
    cursor: "pointer",
    background: active ? "rgba(98,216,216,0.1)" : "transparent",
    color: active ? "var(--color-teal)" : "var(--color-bone-dim)",
    border: "1px solid",
    borderColor: active ? "rgba(98,216,216,0.3)" : "rgba(255,255,255,0.08)",
    borderRadius: "var(--radius-sm)",
    transition: "background 0.15s, color 0.15s, border-color 0.15s",
  };
}

export function ReportPreview({ payload: _payload, ctx, emailBody }: Props) {
  const [tab, setTab] = useState<Tab>("pdf");

  // NOTE: PDF preview via renderToString is not available in the browser bundle.
  // The EndAdvertiserReport template lives in src/reports/ (outside web/) and
  // tsconfig.web.json's include boundary prevents cross-tree imports. The PDF
  // is generated server-side by the Cloudflare Worker (Task 9 / render pipeline).
  // Task 13 (QueuePage) can implement a "render preview" endpoint call if needed.
  const html: string | null = null;

  const emailText = emailBody ?? ctx.commentary ?? "";

  return (
    <div>
      {/* Tab row */}
      <div style={{ display: "flex", gap: 6, marginBottom: 12 }}>
        <button onClick={() => setTab("pdf")} style={tabBtnStyle(tab === "pdf")}>
          PDF preview
        </button>
        <button onClick={() => setTab("email")} style={tabBtnStyle(tab === "email")}>
          Email body
        </button>
      </div>

      {/* PDF tab */}
      {tab === "pdf" && (
        html ? (
          <iframe
            srcDoc={html}
            style={{
              width: "100%",
              height: 600,
              border: "1px solid rgba(255,255,255,0.08)",
              borderRadius: "var(--radius-sm)",
              background: "#fff",
            }}
            title="Report PDF preview"
          />
        ) : (
          <div
            style={{
              height: 200,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "var(--fs-sm)",
              color: "var(--color-bone-dim)",
              background: "rgba(255,255,255,0.02)",
              border: "1px solid rgba(255,255,255,0.06)",
              borderRadius: "var(--radius-sm)",
              flexDirection: "column",
              gap: 8,
            }}
          >
            <span>Preview unavailable — approve to render PDF</span>
            <span style={{ fontSize: "var(--fs-xs)" }}>
              The live preview requires the report to be rendered server-side.
            </span>
          </div>
        )
      )}

      {/* Email body tab */}
      {tab === "email" && (
        <pre
          style={{
            whiteSpace: "pre-wrap",
            fontFamily: "var(--font-sans)",
            fontSize: "var(--fs-sm)",
            lineHeight: 1.7,
            padding: 16,
            background: "rgba(255,255,255,0.03)",
            color: "var(--color-bone)",
            border: "1px solid rgba(255,255,255,0.08)",
            borderRadius: "var(--radius-sm)",
            margin: 0,
            minHeight: 120,
          }}
        >
          {emailText.trim() || (
            <span style={{ color: "var(--color-bone-dim)", fontStyle: "italic" }}>
              (no email body — commentary will be used)
            </span>
          )}
        </pre>
      )}
    </div>
  );
}
